import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig';

interface FormErrors {
  vehicleStatusName: string;
  selectedVehicleStatus: string;
}

interface VehicleStatus {
  id: number;
  statusName: string;
}

interface VehicleStatusData {
  status: string;
  data: VehicleStatus[];
  statusDescription?: string;
}

const AddVehicleStatus: React.FC = () => {
  const [vehicleStatusData, setVehicleStatusData] = useState<VehicleStatusData | null>(null);
  const [isVehicleStatusLoading, setIsVehicleStatusLoading] = useState<boolean>(true);
  const [selectedVehicleStatusId, setSelectedVehicleStatusId] = useState<string>("");
  const [newVehicleStatusName, setNewVehicleStatusName] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    vehicleStatusName: "",
    selectedVehicleStatus: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Operation Failed");

  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle statuses on component mount
  useEffect(() => {
    fetchVehicleStatuses();
  }, []);

  const fetchVehicleStatuses = () => {
    setIsVehicleStatusLoading(true);
    apiDefinitions.getAllVehicleStatuses()
      .then((response: { data: VehicleStatusData }) => {
        if (response.data.status === "S1000" && response.data.data) {
          setVehicleStatusData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle statuses");
          setOpenFail(true);
        }
        setIsVehicleStatusLoading(false);
      })
      .catch((error: any) => {
        setErrorMessage(error.response?.data?.statusDescription || "Failed to load vehicle statuses: Server error");
        setOpenFail(true);
        setIsVehicleStatusLoading(false);
      });
  };

  const validateVehicleStatusForm = (name: string): boolean => {
    const errors: FormErrors = { vehicleStatusName: "", selectedVehicleStatus: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehicleStatusName = "Vehicle status name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehicleStatus = (): boolean => {
    const errors: FormErrors = { vehicleStatusName: "", selectedVehicleStatus: "" };
    let isValid = true;

    if (!selectedVehicleStatusId) {
      errors.selectedVehicleStatus = "Please select a vehicle status";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehicleStatusSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehicleStatusForm(newVehicleStatusName)) return;

    apiDefinitions.createVehicleStatus({ statusName: newVehicleStatusName })
      .then((response: { data: VehicleStatusData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Vehicle status added successfully");
          setNewVehicleStatusName("");
          fetchVehicleStatuses();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle status with this name already exists.");
        } else {
          setErrorMessage(response.data.statusDescription || "Vehicle status creation failed");
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A vehicle status with this name already exists.");
        } else {
          setErrorMessage(error.response?.data?.statusDescription || "Failed to create vehicle status: Server error");
          setOpenFail(true);
        }
      });
  };

  const handleDeleteVehicleStatus = () => {
    if (!validateSelectedVehicleStatus()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleStatus(parseInt(selectedVehicleStatusId))
        .then((response: { data: VehicleStatusData }) => {
          if (response.data.status === "S1000") {
            setSelectedVehicleStatusId("");
            fetchVehicleStatuses();
          } else if (response.data.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("This vehicle status is already in use and cannot be deleted.");
          } else {
             showItemAlreadyUsed("This vehicle status is already in use and cannot be deleted.");
          }
        })
        .catch((error: any) => {
          if (error.response?.data?.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("This vehicle status is already in use and cannot be deleted.");
          } else {
             showItemAlreadyUsed("This vehicle status is already in use and cannot be deleted.");
          }
        });
    });
  };

  const handleVehicleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicleStatusId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehicleStatus: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Vehicle Status</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Status</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehicleStatusName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleStatusName(e.target.value)}
                        placeholder="Enter vehicle status name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-status-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Status
                      </button>
                    </div>
                    {formErrors.vehicleStatusName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleStatusName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehicleStatusId}
                      onChange={handleVehicleStatusChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Status</option>
                      {isVehicleStatusLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        vehicleStatusData?.data?.map((vehicleStatus: VehicleStatus) => (
                          <option key={vehicleStatus.id} value={vehicleStatus.id}>{vehicleStatus.statusName}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedVehicleStatus && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehicleStatus}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehicleStatus}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Status Modal */}
      <div className="modal fade" id="vehicle-status-modal" tabIndex={-1} aria-labelledby="add-vehicle-status" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-status">Add New Vehicle Status</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehicleStatusSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehicleStatusName" className="form-label">Vehicle Status Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehicleStatusName"
                    value={newVehicleStatusName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVehicleStatusName(e.target.value)}
                    placeholder="Enter vehicle status name (e.g., Available)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehicleStatusName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleStatusName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Status
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehicleStatus;